import { getApiClient } from "@src/api";
import { graphql } from "@src/gql";
import { useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod";

const productInformationQuery = graphql(/* GraphQL */ `
	query ProductInformation {
		plans {
			checkout {
				pro {
					annual {
						id
					}
					monthly {
						id
					}
				}
				starter {
					annual {
						id
					}
					monthly {
						id
					}
				}
			}
		}
	}
`);

const priceIdSchema = z.object({
	id: z.string().nullable(),
});

const planPricesSchema = z.object({
	annual: priceIdSchema,
	monthly: priceIdSchema,
});

const checkoutSchema = z.object({
	pro: planPricesSchema,
	starter: planPricesSchema,
});

const productInformationSchema = z.object({
	checkout: checkoutSchema.nullable(),
});

export type ProductInformation = z.infer<typeof productInformationSchema>;
export const productInformationQueryKeys = {
	all: ["productInformation"] as const,
};

export const useProductInformationQuery = () => {
	return useSuspenseQuery({
		queryKey: productInformationQueryKeys.all,
		queryFn: async () => {
			const client = getApiClient();

			const { data } = await client.query({
				query: productInformationQuery,
			});

			return data.plans;
		},
		select: (plans) => {
			const parsedData = productInformationSchema.parse(plans);

			return {
				priceIds: {
					proMonthly: parsedData.checkout?.pro.monthly.id ?? null,
					proAnnual: parsedData.checkout?.pro.annual.id ?? null,
					starterMonthly: parsedData.checkout?.starter.monthly.id ?? null,
					starterAnnual: parsedData.checkout?.starter.annual.id ?? null,
				},
			};
		},
	});
};
